import React, { useRef, useEffect, useMemo } from 'react';
import * as d3 from 'd3';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import { useTheme } from '../context/ThemeContext';

// Dynamic Nivo theme that adapts to light/dark mode
const getNivoTheme = (isDark = true) => ({
  background: 'transparent',
  text: {
    fontSize: 11,
    fill: isDark ? '#e5e7eb' : '#374151',
    outlineWidth: 0,
    outlineColor: 'transparent',
  },
  axis: {
    domain: {
      line: {
        stroke: isDark ? '#374151' : '#d1d5db',
        strokeWidth: 1,
      },
    },
    legend: {
      text: {
        fontSize: 12,
        fill: isDark ? '#9ca3af' : '#6b7280',
      },
    },
    ticks: {
      line: {
        stroke: isDark ? '#374151' : '#d1d5db',
        strokeWidth: 1,
      },
      text: {
        fontSize: 11,
        fill: isDark ? '#e5e7eb' : '#374151',
      },
    },
  },
  grid: {
    line: {
      stroke: isDark ? '#374151' : '#d1d5db',
      strokeWidth: 1,
    },
  },
  legends: {
    title: {
      text: {
        fontSize: 11,
        fill: isDark ? '#e5e7eb' : '#374151',
      },
    },
    text: {
      fontSize: 11,
      fill: isDark ? '#e5e7eb' : '#374151',
    },
  },
});

const PricingTimeSeriesCharts = ({ 
  data, 
  loading, 
  selectedMineral, 
  selectedYear 
}) => {
  const { isDarkMode } = useTheme();
  const nivoTheme = getNivoTheme(isDarkMode);
  const leftChartRef = useRef();
  const rightChartRef = useRef();

  // Format currency function for USD/T
  const formatCurrency = (value) => {
    if (value >= 1000000) {
      return `$${parseFloat((value / 1000000).toFixed(2))}M`;
    } else if (value >= 1000) {
      return `$${parseFloat((value / 1000).toFixed(2))}K`;
    } else {
      return `$${parseFloat(value.toFixed(2))}`;
    }
  };

  // Helper function to validate and parse dates for Nivo time scale
  const isValidDateForNivo = (dateString) => {
    if (!dateString || typeof dateString !== 'string' || dateString.trim() === '') return false;
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return false;
      // Ensure date is valid and can be parsed by Nivo
      return date instanceof Date && !isNaN(date.getTime());
    } catch (e) {
      return false;
    }
  };

  // Process data for charts
  const processedData = useMemo(() => {
    if (!data) return { pricingData: [], monthlyGrowth: [], yoyGrowth: [], rollingAverages: [], summaryData: {} };

    // Debug: Log the raw data from API
    console.log('Raw data from API:', data);
    console.log('Raw yoyGrowth data:', data.yoyGrowth);
    console.log('Raw monthlyGrowth data:', data.monthlyGrowth);
    console.log('Selected year:', selectedYear);

    const { monthlyGrowth = [], yoyGrowth = [], rollingAverages = [], summaryData = {} } = data;

    // Process pricing data from the API response
    const { pricingData: rawPricingData = [] } = data;
    const pricingData = rawPricingData
      .filter(item => item && item.pricing_date != null && item.pricing_date !== '' && isValidDateForNivo(item.pricing_date)) // Filter out null/empty/invalid dates
      .map(item => {
        try {
          const date = new Date(item.pricing_date);
          if (isNaN(date.getTime())) return null;
          return {
            x: date.toISOString().split('T')[0], // Ensure ISO format for Nivo
            y: item.value != null && !isNaN(item.value) ? Number(item.value) : 0
          };
        } catch (e) {
          return null;
        }
      })
      .filter(item => item != null && item.x != null && item.y !== null && !isNaN(item.y) && item.y > 0)
      .sort((a, b) => {
        try {
          return new Date(a.x) - new Date(b.x);
        } catch (e) {
          return 0;
        }
      });
    
    // Debug logging to help identify missing months
    if (pricingData.length > 0) {
      console.log('Pricing data dates:', pricingData.map(d => d.x));
    }
    
    // Extract currency unit from the first record (all are now USD/T)
    const currencyUnit = rawPricingData.length > 0 ? rawPricingData[0].currency || 'USD/T' : 'USD/T';

    // Process monthly growth data
    const monthlyGrowthData = monthlyGrowth
      .filter(item => item && item.calculation_date != null && item.calculation_date !== '' && isValidDateForNivo(item.calculation_date)) // Filter out null/empty/invalid dates
      .map(item => {
        try {
          const date = new Date(item.calculation_date);
          if (isNaN(date.getTime())) return null;
          return {
            x: date.toISOString().split('T')[0], // Ensure ISO format for Nivo
            y: item.monthly_growth_percent != null && !isNaN(item.monthly_growth_percent) ? Number(item.monthly_growth_percent) : 0,
            value: item.value
          };
        } catch (e) {
          return null;
        }
      })
      .filter(item => item != null && item.x != null && item.y !== null && !isNaN(item.y))
      .sort((a, b) => {
        try {
          return new Date(a.x) - new Date(b.x);
        } catch (e) {
          return 0;
        }
      });
    
    // Debug logging for monthly growth data
    if (monthlyGrowthData.length > 0) {
      console.log('Monthly growth data dates:', monthlyGrowthData.map(d => d.x));
      console.log('First date:', monthlyGrowthData[0]?.x);
      console.log('Last date:', monthlyGrowthData[monthlyGrowthData.length - 1]?.x);
      console.log('All monthly growth data:', monthlyGrowthData);
      console.log('January data check:', monthlyGrowthData.filter(d => d.x.includes('2024-01')));
      console.log('January data check (any year):', monthlyGrowthData.filter(d => d.x.includes('-01-')));
    }

    // Process YoY growth data - separate current and previous year
    const currentYear = selectedYear === 'All' ? new Date().getFullYear() : parseInt(selectedYear);
    const previousYear = currentYear - 1;
    
    const currentYearData = yoyGrowth
      .filter(item => item && item.calculation_date != null && item.calculation_date !== '' && isValidDateForNivo(item.calculation_date)) // Filter out null/empty/invalid dates
      .filter(item => {
        try {
          const itemYear = parseInt(item.calculation_date.split('-')[0]);
          return itemYear === currentYear;
        } catch (e) {
          return false;
        }
      })
      .map(item => {
        try {
          // Normalize x-axis to month format for overlapping comparison
          const date = new Date(item.calculation_date);
          if (isNaN(date.getTime())) return null; // Invalid date
          const normalizedDate = `${currentYear}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
          // Validate the normalized date
          if (!isValidDateForNivo(normalizedDate)) return null;
          return {
            x: normalizedDate,
            y: item.yoy_growth_percent != null && !isNaN(item.yoy_growth_percent) ? Number(item.yoy_growth_percent) : 0,
            value: item.value
          };
        } catch (e) {
          return null;
        }
      })
      .filter(item => item != null && item.x != null && item.y !== null && !isNaN(item.y))
      .sort((a, b) => {
        try {
          return new Date(a.x) - new Date(b.x);
        } catch (e) {
          return 0;
        }
      });
    
    const previousYearData = yoyGrowth
      .filter(item => item && item.calculation_date != null && item.calculation_date !== '' && isValidDateForNivo(item.calculation_date)) // Filter out null/empty/invalid dates
      .filter(item => {
        try {
          const itemYear = parseInt(item.calculation_date.split('-')[0]);
          return itemYear === previousYear;
        } catch (e) {
          return false;
        }
      })
      .map(item => {
        try {
          // Normalize x-axis to month format for overlapping comparison
          const date = new Date(item.calculation_date);
          if (isNaN(date.getTime())) return null; // Invalid date
          const normalizedDate = `${currentYear}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
          // Validate the normalized date
          if (!isValidDateForNivo(normalizedDate)) return null;
          return {
            x: normalizedDate,
            y: item.yoy_growth_percent != null && !isNaN(item.yoy_growth_percent) ? Number(item.yoy_growth_percent) : 0,
            value: item.value
          };
        } catch (e) {
          return null;
        }
      })
      .filter(item => item != null && item.x != null && item.y !== null && !isNaN(item.y))
      .sort((a, b) => {
        try {
          return new Date(a.x) - new Date(b.x);
        } catch (e) {
          return 0;
        }
      });
    
    // Debug logging for YoY growth data
    console.log('Current year data:', currentYearData);
    console.log('Previous year data:', previousYearData);
    console.log('Current year:', currentYear, 'Previous year:', previousYear);


    // Process rolling averages data
    const rollingAveragesData = rollingAverages
      .filter(item => item && item.calculation_date != null && item.calculation_date !== '' && isValidDateForNivo(item.calculation_date)) // Filter out null/empty/invalid dates
      .map(item => {
        try {
          const date = new Date(item.calculation_date);
          if (isNaN(date.getTime())) return null;
          return {
            x: date.toISOString().split('T')[0], // Ensure ISO format for Nivo
            '3M Avg': item.rolling_3m_avg != null && !isNaN(item.rolling_3m_avg) ? Number(item.rolling_3m_avg) : 0,
            '6M Avg': item.rolling_6m_avg != null && !isNaN(item.rolling_6m_avg) ? Number(item.rolling_6m_avg) : 0,
            '12M Avg': item.rolling_12m_avg != null && !isNaN(item.rolling_12m_avg) ? Number(item.rolling_12m_avg) : 0,
            value: item.value
          };
        } catch (e) {
          return null;
        }
      })
      .filter(item => item != null && item.x != null && isValidDateForNivo(item.x) && (item['3M Avg'] !== null || item['6M Avg'] !== null || item['12M Avg'] !== null))
      .sort((a, b) => {
        try {
          const dateA = new Date(a.x);
          const dateB = new Date(b.x);
          if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) return 0;
          return dateA - dateB;
        } catch (e) {
          return 0;
        }
      });

    return {
      pricingData,
      monthlyGrowth: monthlyGrowthData,
      yoyGrowth: {
        currentYear: currentYearData,
        previousYear: previousYearData
      },
      rollingAverages: rollingAveragesData,
      summaryData,
      currencyUnit
    };
  }, [data, selectedYear]);

  // Loading component
  if (loading) {
    return (
      <div className="space-y-6">
        {/* Pricing Data Loading */}
        <div className="rounded-lg shadow-md p-6 border border-gray-600">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded mb-4"></div>
            <div className="h-64 bg-gray-300 dark:bg-gray-600 rounded"></div>
          </div>
        </div>
        
        {/* Growth Charts Loading */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-lg shadow-md p-6 border border-gray-600">
            <div className="animate-pulse">
              <div className="h-6 bg-gray-600 rounded mb-4"></div>
              <div className="h-64 bg-gray-600 rounded"></div>
            </div>
          </div>
          <div className="rounded-lg shadow-md p-6 border border-gray-600">
            <div className="animate-pulse">
              <div className="h-6 bg-gray-600 rounded mb-4"></div>
              <div className="h-64 bg-gray-600 rounded"></div>
            </div>
          </div>
        </div>
        
        {/* Bottom Charts Loading */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-lg shadow-md p-6 border border-gray-600">
            <div className="animate-pulse">
              <div className="h-6 bg-gray-600 rounded mb-4"></div>
              <div className="h-64 bg-gray-600 rounded"></div>
            </div>
          </div>
          <div className="rounded-lg shadow-md p-6 border border-gray-600">
            <div className="animate-pulse">
              <div className="h-6 bg-gray-600 rounded mb-4"></div>
              <div className="h-64 bg-gray-600 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No data component
  if (!data || (!processedData.pricingData.length && !processedData.monthlyGrowth.length && !processedData.yoyGrowth.length)) {
    return (
      <div className="space-y-6">
        {/* Pricing Data No Data */}
        <div className="bg-white dark:bg-gray-700 rounded-lg p-6 border border-gray-200 dark:border-gray-600 transition-colors duration-200">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Pricing Data</h3>
          <div className="flex items-center justify-center h-64 bg-gray-200 dark:bg-gray-600 rounded">
            <div className="text-center text-gray-600 dark:text-gray-400">
              <div className="text-2xl mb-2">💰</div>
              <div>No pricing data available</div>
            </div>
          </div>
        </div>
        
        {/* Growth Charts No Data */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-700 rounded-lg p-6 border border-gray-200 dark:border-gray-600 transition-colors duration-200">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Monthly Growth</h3>
            <div className="flex items-center justify-center h-64 bg-gray-200 dark:bg-gray-600 rounded">
              <div className="text-center text-gray-600 dark:text-gray-400">
                <div className="text-2xl mb-2">📊</div>
                <div>No monthly growth data available</div>
              </div>
            </div>
          </div>
          <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-6 border border-gray-200 dark:border-gray-600 transition-colors duration-200">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Year-over-Year Growth</h3>
            <div className="flex items-center justify-center h-64 bg-gray-200 dark:bg-gray-600 rounded">
              <div className="text-center text-gray-600 dark:text-gray-400">
                <div className="text-2xl mb-2">📈</div>
                <div>No YoY growth data available</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* First Row: Pricing Data Chart */}
      <div className="rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-600 transition-colors duration-200">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {selectedMineral} Pricing Trends - (USD/T)
        </h3>
        <p>This chart shows {selectedMineral} prices for each month of {selectedYear}. It helps track how prices moved during the year and provides a clear view of monthly pricing patterns. Essential price tracking for understanding cost exposure, budgeting, and forecasting {selectedMineral}-related procurement risks.</p>
        <div className="h-64">
          {processedData.pricingData && processedData.pricingData.length > 0 && processedData.pricingData.every(d => d && d.x && isValidDateForNivo(d.x)) ? (
            <ResponsiveLine
              data={[{
                id: 'pricing-data',
                data: processedData.pricingData.filter(d => d && d.x && isValidDateForNivo(d.x))
              }]}
              margin={{ top: 20, right: 30, bottom: 50, left: 60 }}
              xScale={{
                type: 'time',
                format: '%Y-%m-%d',
                precision: 'day',
              }}
              xFormat="time:%Y-%m-%d"
              yScale={{
                type: 'linear',
                min: 'auto',
                max: 'auto',
                stacked: false,
                reverse: false
              }}
              curve="monotoneX"
              axisTop={null}
              axisRight={null}
              axisBottom={{
                orient: 'bottom',
                format: selectedYear === 'All' ? '%Y-%m' : '%b',
                tickValues: selectedYear === 'All' ? 'every 6 months' : 'every month',
                legend: 'Month',
                legendOffset: 36,
                legendPosition: 'middle',
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0
              }}
              axisLeft={{
                orient: 'left',
                legend: `Price (${processedData.currencyUnit})`,
                legendOffset: -80,
                legendPosition: 'middle',
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0
              }}
              colors={['#3d71b8']}
              pointSize={4}
              pointBorderWidth={2}
              pointBorderColor={{ from: 'serieColor' }}
              pointLabelYOffset={-12}
              useMesh={true}
              tooltip={({ point }) => (
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3 shadow-lg transition-colors duration-200">
                  <div className="text-gray-900 dark:text-white font-semibold">{point.serieId}</div>
                  <div className="text-gray-700 dark:text-gray-300">{new Date(point.data.x).toLocaleDateString()}</div>
                  <div className="text-gray-700 dark:text-gray-300">{formatCurrency(point.data.y)}</div>
                </div>
              )}
              theme={nivoTheme}
            />
          ) : (
            <div className="flex items-center justify-center h-full bg-gray-200 dark:bg-gray-600 rounded">
              <div className="text-center text-gray-600 dark:text-gray-400">
                <div className="text-2xl mb-2">💰</div>
                <div>No pricing data available</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Second Row: Growth Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Growth Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-600 transition-colors duration-200">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {selectedMineral} - Monthly PriceGrowth (%)
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-2 text-xm leading-relaxed">This chart shows the month-to-month percentage change in {selectedMineral} prices. It highlights short-term pricing movements and helps identify months with noticeable shifts. Reveals short-term price volatility and monthly shocks, enabling faster response to sudden market changes. </p>
          <div className="h-64">
            {processedData.monthlyGrowth.length > 0 && processedData.monthlyGrowth.every(d => d && d.x && isValidDateForNivo(d.x)) ? (
              <ResponsiveLine
                data={[{
                  id: 'monthly-growth',
                  data: processedData.monthlyGrowth.filter(d => d && d.x && isValidDateForNivo(d.x))
                }]}
                margin={{ top: 20, right: 30, bottom: 50, left: 60 }}
                xScale={{
                  type: 'time',
                  format: '%Y-%m-%d',
                  precision: 'month',
                  useUTC: false,
                  nice: true,
                  min: 'auto',
                  max: 'auto'
                }}
                xFormat="time:%Y-%m-%d"
                yScale={{
                  type: 'linear',
                  min: 'auto',
                  max: 'auto',
                  stacked: false,
                  reverse: false
                }}
                curve="monotoneX"
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  orient: 'bottom',
                  format: selectedYear === 'All' ? '%Y-%m' : '%b',
                  tickValues: selectedYear === 'All' ? 'every 3 months' : undefined,
                  legend: 'Month',
                  legendOffset: 36,
                  legendPosition: 'middle',
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  tickOffset: 0,
                  tickSpacing: 0,
                  tickPadding: 4
                }}
                axisLeft={{
                  orient: 'left',
                  legend: 'Growth (%)',
                  legendOffset: -50,
                  legendPosition: 'middle',
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0
                }}
                colors={['#3d71b8']}
                pointSize={4}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                pointLabelYOffset={-12}
                useMesh={true}
                tooltip={({ point }) => (
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3 shadow-lg transition-colors duration-200">
                    <div className="text-gray-900 dark:text-white font-semibold">{point.serieId}</div>
                    <div className="text-gray-700 dark:text-gray-300">{new Date(point.data.x).toLocaleDateString()}</div>
                    <div className="text-gray-700 dark:text-gray-300">{parseFloat(point.data.y).toFixed(2)}%</div>
                  </div>
                )}
                theme={nivoTheme}
                enableSlices={false}
                sliceTooltip={({ slice }) => (
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3 shadow-lg transition-colors duration-200">
                    <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                      {slice.points[0]?.data?.xFormatted}
                    </div>
                    {slice.points.map((point) => (
                      <div key={point.id} className="text-sm">
                        <span className="inline-block w-3 h-3 rounded-full mr-2" style={{ backgroundColor: point.color }}></span>
                        {point.serieId}: {point.data.yFormatted}%
                      </div>
                    ))}
                  </div>
                )}
              />
            ) : (
              <div className="flex items-center justify-center h-full bg-gray-600 rounded">
                <div className="text-center text-gray-400">
                  <div className="text-2xl mb-2">📊</div>
                  <div>No monthly growth data available</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* YoY Growth Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-600 transition-colors duration-200">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {selectedMineral} - Year-over-Year Price Growth (%)
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-2 text-xm leading-relaxed">This chart shows how {selectedMineral} prices changed each month compared with the same month in the previous year. It provides a longer-term view of pricing trends and annual shifts. Shows deeper structural price movements. Helps anticipate long-term cost pressures and adjust sourcing strategies accordingly. Rolling averages expose underlying price cycles and early trend reversals—valuable for predicting market cooling, heating, or sustained volatility. </p>
          <div className="h-64">
            {((processedData.yoyGrowth.currentYear.length > 0 && processedData.yoyGrowth.currentYear.every(d => d && d.x && isValidDateForNivo(d.x))) || 
              (processedData.yoyGrowth.previousYear.length > 0 && processedData.yoyGrowth.previousYear.every(d => d && d.x && isValidDateForNivo(d.x)))) ? (
              <ResponsiveLine
                data={[
                  {
                    id: selectedYear === 'All' ? new Date().getFullYear().toString() : selectedYear,
                    data: processedData.yoyGrowth.currentYear.filter(d => d && d.x && isValidDateForNivo(d.x))
                  },
                  {
                    id: selectedYear === 'All' ? (new Date().getFullYear() - 1).toString() : (parseInt(selectedYear) - 1).toString(),
                    data: processedData.yoyGrowth.previousYear.filter(d => d && d.x && isValidDateForNivo(d.x))
                  }
                ].filter(series => series.data && series.data.length > 0)}
                margin={{ top: 20, right: 30, bottom: 80, left: 60 }}
                xScale={{
                  type: 'time',
                  format: '%Y-%m-%d',
                  precision: 'month',
                  useUTC: false,
                  nice: true,
                  min: 'auto',
                  max: 'auto'
                }}
                xFormat="time:%Y-%m-%d"
                yScale={{
                  type: 'linear',
                  min: 'auto',
                  max: 'auto',
                  stacked: false,
                  reverse: false
                }}
                curve="monotoneX"
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  orient: 'bottom',
                  format: selectedYear === 'All' ? '%Y-%m' : '%b',
                    tickValues: selectedYear === 'All' ? 'every 3 months' : undefined,
                    legend: 'Month',
                  legendOffset: 36,
                  legendPosition: 'middle',
                  tickSize: 5,
                  tickPadding: 5,
                    tickRotation: 0,
                    tickOffset: 0,
                    tickSpacing: 0,
                    tickPadding: 8
                }}
                axisLeft={{
                  orient: 'left',
                  legend: 'Growth (%)',
                  legendOffset: -50,
                  legendPosition: 'middle',
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0
                }}
                 colors={['#3d71b8', '#9ca3af']}
                pointSize={4}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                pointLabelYOffset={-12}
                useMesh={true}
                enableSlices="x"
                legends={[
                  {
                    anchor: 'bottom',
                    direction: 'row',
                    justify: false,
                    translateX: 0,
                    translateY: 70,
                    itemsSpacing: 20,
                    itemDirection: 'left-to-right',
                    itemWidth: 80,
                    itemHeight: 20,
                    itemOpacity: 0.75,
                    symbolSize: 12,
                    symbolShape: 'circle',
                    effects: [
                      {
                        on: 'hover',
                        style: {
                          itemBackground: 'rgba(0, 0, 0, .03)',
                          itemOpacity: 1
                        }
                      }
                    ]
                  }
                ]}
                sliceTooltip={({ slice }) => (
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3 shadow-lg transition-colors duration-200">
                    <div className="text-gray-900 dark:text-white font-semibold mb-2">Month: {new Date(slice.points[0].data.x).toLocaleDateString('en-US', { month: 'short' })}</div>
                    {slice.points.map((point, index) => (
                      <div key={index} className="flex items-center gap-2 mb-1">
                        <span 
                          className="inline-block w-3 h-3 rounded-full" 
                          style={{ backgroundColor: point.color }}
                        ></span>
                        <span className="text-gray-700 dark:text-gray-300 text-sm">
                          {point.serieId}: {parseFloat(point.data.y).toFixed(2)}%
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                theme={nivoTheme}
              />
            ) : (
              <div className="flex items-center justify-center h-full bg-gray-600 rounded">
                <div className="text-center text-gray-400">
                  <div className="text-2xl mb-2">📈</div>
                  <div>No YoY growth data available</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Rolling Averages Chart (Full Width) */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-600 transition-colors duration-200">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
           {selectedMineral} -  Price Momentum (Rolling Averages)
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-2 text-xm leading-relaxed">This chart shows 3-month, 6-month, and 12-month rolling averages of {selectedMineral} prices. These smoothed lines highlight the broader price momentum and help identify longer-term pricing trends</p>
          <div className="h-80">
            {processedData.rollingAverages.length > 0 && processedData.rollingAverages.every(item => item && item.x && isValidDateForNivo(item.x)) ? (
              <ResponsiveLine
                data={[
                  {
                    id: '3M Avg',
                    data: processedData.rollingAverages
                      .filter(item => item && item.x && isValidDateForNivo(item.x))
                      .map(item => ({ x: item.x, y: item['3M Avg'] }))
                      .filter(point => point && point.x && point.y != null && !isNaN(point.y))
                  },
                  {
                    id: '6M Avg',
                    data: processedData.rollingAverages
                      .filter(item => item && item.x && isValidDateForNivo(item.x))
                      .map(item => ({ x: item.x, y: item['6M Avg'] }))
                      .filter(point => point && point.x && point.y != null && !isNaN(point.y))
                  },
                  {
                    id: '12M Avg',
                    data: processedData.rollingAverages
                      .filter(item => item && item.x && isValidDateForNivo(item.x))
                      .map(item => ({ x: item.x, y: item['12M Avg'] }))
                      .filter(point => point && point.x && point.y != null && !isNaN(point.y))
                  }
                ].filter(series => series.data && series.data.length > 0)}
                margin={{ top: 20, right: 30, bottom: 80, left: 80 }}
                xScale={{
                  type: 'time',
                  format: '%Y-%m-%d',
                    precision: 'month',
                }}
                xFormat="time:%Y-%m-%d"
                yScale={{
                  type: 'linear',
                  min: 'auto',
                  max: 'auto',
                  stacked: false,
                  reverse: false
                }}
                curve="monotoneX"
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  orient: 'bottom',
                  format: selectedYear === 'All' ? '%Y-%m' : '%b',
                  tickValues: selectedYear === 'All' ? 'every 3 months' : 'every month',
                  legend: 'Month',
                  legendOffset: 36,
                  legendPosition: 'middle',
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0
                }}
              axisLeft={{
                orient: 'left',
                legend: `Price (${processedData.currencyUnit})`,
                legendOffset: -50,
                legendPosition: 'middle',
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                format: formatCurrency
              }}
                 colors={['#8bb3a0', '#d4a574', '#7c8db0']}
                pointSize={3}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                pointLabelYOffset={-12}
                useMesh={true}
                legends={[
                  {
                    anchor: 'bottom',
                    direction: 'row',
                    justify: false,
                    translateX: 0,
                    translateY: 80,
                    itemsSpacing: 20,
                    itemDirection: 'left-to-right',
                    itemWidth: 80,
                    itemHeight: 20,
                    itemOpacity: 0.75,
                    symbolSize: 12,
                    symbolShape: 'circle',
                    effects: [
                      {
                        on: 'hover',
                        style: {
                          itemBackground: 'rgba(0, 0, 0, .03)',
                          itemOpacity: 1
                        }
                      }
                    ]
                  }
                ]}
                tooltip={({ point }) => (
                  <div className="bg-gray-800 border border-gray-600 rounded-lg p-3 shadow-lg">
                    <div className="text-white font-semibold">{point.serieId}</div>
                    <div className="text-gray-300">{new Date(point.data.x).toLocaleDateString()}</div>
                    <div className="text-gray-300">{formatCurrency(point.data.y)}</div>
                  </div>
                )}
                theme={nivoTheme}
              />
            ) : (
              <div className="flex items-center justify-center h-full bg-gray-600 rounded">
                <div className="text-center text-gray-400">
                  <div className="text-2xl mb-2">📊</div>
                  <div>No rolling averages data available</div>
                </div>
              </div>
            )}
          </div>
      </div>

      {/* Summary Statistics (Below Rolling Averages)
      <div>
          {/* <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Summary Statistics - {selectedMineral}
          </h3> */}
          {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {processedData.summaryData && Object.keys(processedData.summaryData).length > 0 ? (
              Object.entries(processedData.summaryData)
                .filter(([key]) => {
                  // Filter out HS Code related fields
                  const lowerKey = key.toLowerCase();
                  return !lowerKey.includes('hs') && !lowerKey.includes('unified') && !lowerKey.includes('code');
                })
                .map(([key, value]) => {
                // Handle nested objects properly
                if (value && typeof value === 'object') {
                  // Filter nested objects as well
                  const filteredValue = Object.fromEntries(
                    Object.entries(value).filter(([subKey]) => {
                      const lowerSubKey = subKey.toLowerCase();
                      return !lowerSubKey.includes('hs') && !lowerSubKey.includes('unified') && !lowerSubKey.includes('code');
                    })
                  );
                  
                  // Only render if there are remaining fields after filtering
                  if (Object.keys(filteredValue).length === 0) {
                    return null;
                  }
                  
                  return (
                    <div key={key} className="bg-gray-200 dark:bg-gray-600 rounded-lg p-4 transition-colors duration-200">
                      <div className="text-gray-700 dark:text-gray-300 font-medium capitalize mb-3">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </div>
                      <div className="space-y-2">
                        {Object.entries(filteredValue).map(([subKey, subValue]) => (
                          <div key={subKey} className="flex justify-between items-center">
                            <span className="text-gray-700 dark:text-gray-300 text-sm">
                              {subKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                            <span className="text-gray-900 dark:text-white font-semibold text-sm">
                              {typeof subValue === 'number' ? subValue.toLocaleString() : String(subValue || 'N/A')}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div key={key} className="bg-gray-200 dark:bg-gray-600 rounded-lg p-4 transition-colors duration-200">
                      <div className="text-gray-700 dark:text-gray-300 font-medium capitalize mb-2">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </div>
                      <div className="text-gray-900 dark:text-white font-semibold">
                        {typeof value === 'number' ? value.toLocaleString() : String(value || 'N/A')}
                      </div>
                    </div>
                  );
                }
              }).filter(Boolean)
            ) : (
              <div className="col-span-full flex items-center justify-center h-48 bg-gray-200 dark:bg-gray-600 rounded">
                <div className="text-center text-gray-600 dark:text-gray-400">
                  <div className="text-2xl mb-2">📋</div>
                  <div>No summary data available</div>
                </div>
              </div>
            )}
          </div> 
        </div>*/}
    </div>
  );
};

export default PricingTimeSeriesCharts;
